import * as React from "react";
import { Box, Button, Typography, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { ActiveRallyVideo } from "./ActiveRallyVideo";

import type { KitchenArrivalRow } from "./RallyBoardPopup";
import {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";

/*
========================================
Types
========================================
*/

type IdentityKind =
  | "nextFocus"
  | "rallyLength"
  | "errorEnvironment"
  | "teamShortRallyEdge"
  | "teamErrorLeak"
  | "kitchenRole"
  | "exchangeType";

type IdentityExample = {
  key: string;
  rallyIndex: number;
  rallyNumber: number;
  team: 0 | 1 | null;
  playerId: number | null;
  shotIndices: number[];
  reason: string;
};

type IdentityRow = {
  kind: IdentityKind;
  scope: "focus" | "match" | "team";
  team?: 0 | 1;
  edgeTeam?: 0 | 1 | null;
  label: string;
  quantity: string;
  explanation: string;
  examples: IdentityExample[];
};

type IdentityResult = {
  oneSentence: string;
  rows: IdentityRow[];
};

type PlayerFocusCard = {
  playerId: number;
  label: string;
  detail: string;
  examples: IdentityExample[];
};

function focusHelpForLabel(label: string) {
  const text = label.toLowerCase();

  if (
    text.includes("point-ending misses") ||
    text.includes("terminal miss")
  ) {
    return {
      means:
        "Look at the final miss in these rallies. Decide whether it was caused by pressure, balance, shot choice, or a routine execution miss.",
      drills:
        "Drill: play out points where the goal is a neutral ball before attacking. Add a rule that speedups or attacks only count after balance is recovered.",
    };
  }

  if (
    text.includes("first-four-shot") ||
    text.includes("first four") ||
    text.includes("early pressure")
  ) {
    return {
      means:
        "The point is being shaped before the rally settles: serve, return, third shot, and fourth shot. Review who gets pressured first and why.",
      drills:
        "Drill: serve-return-third-fourth only. Replay the first four balls until the return is deep, the third is playable, and the fourth does not give away an attack.",
    };
  }

  if (text.includes("kitchen control")) {
    return {
      means:
        "One side is getting comfortable at the kitchen: stable feet, controlled dinks/resets, and fewer balls they have to fight off.",
      drills:
        "Drill: transition-to-kitchen games. Start midcourt, require two neutral resets or dinks before attacking, and score only when the team reaches a stable kitchen position.",
    };
  }

  if (text.includes("rally pattern")) {
    return {
      means:
        "These rallies share a repeated shape. Name the pattern first: drive exchange, dink/drop exchange, transition reset, speedup sequence, or attack after a sitter.",
      drills:
        "Drill: pattern replay. Recreate the first 4-6 shots from the clip, then play it out and test one adjustment at a time.",
    };
  }

  if (text.includes("uncomfortable finishes")) {
    return {
      means:
        "The opponent is missing because the previous ball made the finish uncomfortable: depth, pace, body pressure, low contact, or patience pressure.",
      drills:
        "Drill: pressure without over-attacking. Feed balls that force the opponent to hit from low contact or stretched positions, then keep the next ball in play.",
    };
  }

  return {
    means:
      "Use the evidence clips to name what repeats, then decide whether the fix is shot choice, court position, balance, or execution.",
    drills:
      "Drill: replay the same situation several times and change only one variable: target, height, pace, or recovery position.",
  };
}

function playerFocusLabelFromRow(row: IdentityRow) {
  const label = row.label.replace(/^Team \d+: /, "");

  if (row.kind === "nextFocus") return `Primary focus: ${label}`;
  if (row.kind === "teamErrorLeak" || row.kind === "errorEnvironment") {
    return `Primary focus: review point-ending misses`;
  }
  if (row.kind === "teamShortRallyEdge" || row.kind === "rallyLength") {
    return `Primary focus: first-four-shot pressure`;
  }
  if (row.kind === "kitchenRole") {
    return `Primary focus: kitchen control`;
  }
  if (row.kind === "exchangeType") {
    return `Primary focus: repeated rally pattern`;
  }

  return `Primary focus: ${label}`;
}

/*
========================================
Helpers
========================================
*/

function makeExample(
  rallyIndex: number,
  shotIndices: number[],
  reason: string,
  team: 0 | 1 | null = null,
  playerId: number | null = null,
): IdentityExample {
  return {
    key: `r${rallyIndex}_${shotIndices.join("_")}_${reason}`,
    rallyIndex,
    rallyNumber: rallyIndex + 1,
    team,
    playerId,
    shotIndices,
    reason,
  };
}

function rallyLength(row: KitchenArrivalRow) {
  return row.numShots ?? row.shotNumberOverlays?.length ?? 0;
}

function winningTeam(row: KitchenArrivalRow): 0 | 1 | null {
  return row.winnerTeam ?? null;
}

function losingTeam(row: KitchenArrivalRow): 0 | 1 | null {
  const winner = winningTeam(row);
  if (winner == null) return null;
  return winner === 0 ? 1 : 0;
}

function lastOverlay(row: KitchenArrivalRow) {
  const overlays = row.shotNumberOverlays ?? [];
  return overlays[overlays.length - 1];
}

function overlayPlayerId(o: any): number | null {
  const pid = o?.playerId ?? o?.rawShot?.player_id;
  return typeof pid === "number" && Number.isFinite(pid) ? pid : null;
}

function teamForPlayerId(playerId: number | null): 0 | 1 | null {
  if (playerId === 0 || playerId === 1) return 0;
  if (playerId === 2 || playerId === 3) return 1;
  return null;
}

function overlayTeam(o: any): 0 | 1 | null {
  return teamForPlayerId(overlayPlayerId(o));
}

function shotPlayerId(row: KitchenArrivalRow, shotIndex: number | null) {
  if (shotIndex == null) return null;
  const overlay = row.shotNumberOverlays?.[shotIndex];
  return overlayPlayerId(overlay);
}

function getShotTypeFromOverlay(o: any): string | null {
  if (typeof o?.shotType === "string") return o.shotType;
  if (typeof o?.rawShot?.shot_type === "string") return o.rawShot.shot_type;

  const keys = Object.keys(o?.rawShot?.tags ?? {});
  if (keys.some((k) => k.includes("type;drive"))) return "drive";
  if (keys.some((k) => k.includes("type;drop"))) return "drop";
  if (keys.some((k) => k.includes("type;dink"))) return "dink";
  if (keys.some((k) => k.includes("type;smash"))) return "smash";
  if (keys.some((k) => k.includes("type;lob"))) return "lob";
  if (keys.some((k) => k.includes("type;serve"))) return "serve";

  return null;
}

function terminalErrorInfo(row: KitchenArrivalRow): {
  shotIndex: number;
  team: 0 | 1 | null;
  playerId: number | null;
  kind: "net" | "out" | "net/out";
} | null {
  const overlays = row.shotNumberOverlays ?? [];
  const shotIndex = Math.max(0, overlays.length - 1);
  const last = lastOverlay(row);
  const s = last?.rawShot;

  if (!s) return null;

  const endZone = s.resulting_ball_movement?.trajectory?.end?.zone;
  if (endZone === "out") {
    return {
      shotIndex,
      team: overlayTeam(last),
      playerId: overlayPlayerId(last),
      kind: "out",
    };
  }
  if (endZone === "net") {
    return {
      shotIndex,
      team: overlayTeam(last),
      playerId: overlayPlayerId(last),
      kind: "net",
    };
  }

  const faults = s.errors?.faults;
  if (!faults) return null;

  const str = JSON.stringify(faults).toLowerCase();
  const hasNet = str.includes("net") || str.includes("tape");
  const hasOut = str.includes("out");
  if (!hasNet && !hasOut) return null;

  return {
    shotIndex,
    team: overlayTeam(last),
    playerId: overlayPlayerId(last),
    kind: hasNet && hasOut ? "net/out" : hasNet ? "net" : "out",
  };
}

function kitchenShotIndices(row: KitchenArrivalRow) {
  const overlays = row.shotNumberOverlays ?? [];

  return overlays
    .map((o, shotIndex) => {
      const type = getShotTypeFromOverlay(o);
      const startZone = o.trajectoryStart?.zone;
      const endZone = o.trajectoryEnd?.zone;

      return type === "dink" || startZone === "kitchen" || endZone === "kitchen"
        ? shotIndex
        : null;
    })
    .filter((shotIndex): shotIndex is number => shotIndex != null);
}

function isAttackOverlay(o: any) {
  const type = getShotTypeFromOverlay(o);

  return (
    type === "drive" ||
    type === "smash" ||
    o.rawShot?.is_speedup === true
  );
}

function isSoftOverlay(o: any) {
  const type = getShotTypeFromOverlay(o);
  return type === "drop" || type === "dink";
}

function pctText(count: number, total: number) {
  const pct = total === 0 ? 0 : count / total;
  return `${Math.round(pct * 100)}%`;
}

function edgeTeamFromWins(
  winsByTeam: [number, number],
  total: number,
): 0 | 1 | null {
  if (total <= 0) return null;
  const edgeTeam: 0 | 1 = winsByTeam[0] >= winsByTeam[1] ? 0 : 1;
  const edgeWins = winsByTeam[edgeTeam];
  return edgeWins / total >= 0.58 ? edgeTeam : null;
}

function teamEdgeText(winsByTeam: [number, number], total: number): string {
  if (total <= 0) return "No team split available for this pattern.";

  const [team0Wins, team1Wins] = winsByTeam;
  const edgeTeam = edgeTeamFromWins(winsByTeam, total);

  if (edgeTeam == null) {
    return `Balanced between teams: Team 0 won ${team0Wins}, Team 1 won ${team1Wins}.`;
  }

  const edgeWins = winsByTeam[edgeTeam];
  const edgePct = edgeWins / total;

  return `Team ${edgeTeam} had the edge: ${edgeWins} of ${total} (${Math.round(
    edgePct * 100,
  )}%).`;
}

function teamSplitText(winsByTeam: [number, number], total: number): string {
  if (total <= 0) return "No examples found.";
  return `Team 0 ${winsByTeam[0]}, Team 1 ${winsByTeam[1]}`;
}

function quantityWithTeamSplit(
  quantity: string,
  winsByTeam: [number, number],
  total: number,
): string {
  if (total <= 0) return quantity;
  return `${quantity} · ${teamSplitText(winsByTeam, total)}`;
}

function labelWithEdge(
  baseLabel: string,
  edgeLabel: string,
  winsByTeam: [number, number],
  total: number,
): string {
  const edgeTeam = edgeTeamFromWins(winsByTeam, total);
  return edgeTeam == null ? baseLabel : `${edgeLabel} Team ${edgeTeam}`;
}

function sentenceCase(text: string): string {
  return text.length === 0 ? text : text[0].toUpperCase() + text.slice(1);
}

function topExamplePlayer(examples: IdentityExample[]) {
  const counts = new Map<number, number>();

  examples.forEach((ex) => {
    if (ex.playerId == null) return;
    counts.set(ex.playerId, (counts.get(ex.playerId) ?? 0) + 1);
  });

  let playerId: number | null = null;
  let count = 0;

  counts.forEach((candidateCount, candidatePlayerId) => {
    if (candidateCount <= count) return;
    playerId = candidatePlayerId;
    count = candidateCount;
  });

  if (playerId == null || count === 0) return null;

  return {
    playerId,
    count,
    total: examples.filter((ex) => ex.playerId != null).length,
  };
}

function playerFocusSuffix(examples: IdentityExample[]) {
  const top = topExamplePlayer(examples);
  if (!top || top.count < 2) return "";

  return ` Most tagged examples point to p${top.playerId} (${top.count} of ${top.total}).`;
}

function exampleWinnerSort(
  rows: KitchenArrivalRow[],
  edgeTeam: 0 | 1 | null,
) {
  return (a: IdentityExample, b: IdentityExample) => {
    const aw = winningTeam(rows[a.rallyIndex]);
    const bw = winningTeam(rows[b.rallyIndex]);
    if (edgeTeam != null && aw !== bw) {
      if (aw === edgeTeam) return -1;
      if (bw === edgeTeam) return 1;
    }
    return rallyLength(rows[b.rallyIndex]) - rallyLength(rows[a.rallyIndex]);
  };
}

/*
========================================
Match-shape rows
========================================
*/

function buildShortRallyRow(rows: KitchenArrivalRow[]): IdentityRow {
  const SHORT_THRESHOLD = 4;
  const candidates: IdentityExample[] = [];
  let shortCount = 0;
  const winsByTeam: [number, number] = [0, 0];

  rows.forEach((row, i) => {
    const len = rallyLength(row);
    if (len > SHORT_THRESHOLD) return;

    shortCount++;
    const winner = winningTeam(row);
    if (winner != null) winsByTeam[winner]++;

    candidates.push(
      makeExample(
        i,
        [],
        `${len}-shot rally resolved early`,
        winner,
        overlayPlayerId(lastOverlay(row)),
      ),
    );
  });

  const pct = rows.length === 0 ? 0 : shortCount / rows.length;
  const edgeTeam = edgeTeamFromWins(winsByTeam, shortCount);
  const examples = candidates
    .sort(
      (a, b) =>
        exampleWinnerSort(rows, edgeTeam)(a, b) ||
        rallyLength(rows[a.rallyIndex]) - rallyLength(rows[b.rallyIndex]),
    )
    .slice(0, 5);

  return {
    kind: "rallyLength",
    scope: "match",
    edgeTeam,
    label: labelWithEdge(
      pct >= 0.5
        ? "Short-rally dominant"
        : pct >= 0.35
          ? "Short-rally leaning"
          : "Mixed rally length",
      "Short rallies favored",
      winsByTeam,
      shortCount,
    ),
    quantity: quantityWithTeamSplit(
      `${pctText(shortCount, rows.length)} ≤ ${SHORT_THRESHOLD} shots`,
      winsByTeam,
      shortCount,
    ),
    explanation: `${
      pct >= 0.5
        ? "Most points resolved early rather than after extended exchanges."
        : pct >= 0.35
          ? "Short rallies were common, but not overwhelming."
          : "Rally length was more mixed than purely short."
    } ${teamEdgeText(winsByTeam, shortCount)}`,
    examples,
  };
}

function buildKitchenRoleRow(rows: KitchenArrivalRow[]): IdentityRow {
  const candidates: IdentityExample[] = [];
  let count = 0;
  const winsByTeam: [number, number] = [0, 0];

  rows.forEach((row, i) => {
    const shotIndices = kitchenShotIndices(row);
    if (shotIndices.length < 2) return;

    count++;
    const winner = winningTeam(row);
    if (winner != null) winsByTeam[winner]++;

    candidates.push(
      makeExample(
        i,
        shotIndices.slice(0, 4),
        `${shotIndices.length} kitchen/dink-linked shots`,
        winner,
        shotPlayerId(row, shotIndices[0]),
      ),
    );
  });

  const pct = rows.length === 0 ? 0 : count / rows.length;
  const edgeTeam = edgeTeamFromWins(winsByTeam, count);
  const examples = candidates
    .sort(
      (a, b) =>
        exampleWinnerSort(rows, edgeTeam)(a, b) ||
        (b.shotIndices.length || 0) - (a.shotIndices.length || 0) ||
        rallyLength(rows[b.rallyIndex]) - rallyLength(rows[a.rallyIndex]),
    )
    .slice(0, 5);

  return {
    kind: "kitchenRole",
    scope: "match",
    edgeTeam,
    label: labelWithEdge(
      pct >= 0.55
        ? "Kitchen was central"
        : pct >= 0.3
          ? "Kitchen was secondary"
          : "Few long dink battles",
      "Kitchen rallies favored",
      winsByTeam,
      count,
    ),
    quantity: quantityWithTeamSplit(
      `${pctText(count, rows.length)} reached kitchen structure`,
      winsByTeam,
      count,
    ),
    explanation: `${
      pct >= 0.55
        ? "Many rallies developed into kitchen or dink exchanges."
        : pct >= 0.3
          ? "Kitchen exchanges appeared, but did not dominate the match."
          : "Most points resolved before extended kitchen exchanges."
    } ${teamEdgeText(winsByTeam, count)}`,
    examples,
  };
}

function buildExchangeTypeRow(rows: KitchenArrivalRow[]): IdentityRow {
  const attackExamples: IdentityExample[] = [];
  const softExamples: IdentityExample[] = [];
  const mixedExamples: IdentityExample[] = [];

  let totalUsableShots = 0;
  let attackShots = 0;
  let softShots = 0;
  let attackRallyCount = 0;
  let softRallyCount = 0;
  const attackWinsByTeam: [number, number] = [0, 0];
  const softWinsByTeam: [number, number] = [0, 0];
  const mixedWinsByTeam: [number, number] = [0, 0];
  let mixedRallyCount = 0;

  rows.forEach((row, rallyIndex) => {
    const overlays = row.shotNumberOverlays ?? [];
    const rallyAttackShotIndices: number[] = [];
    const rallySoftShotIndices: number[] = [];

    overlays.forEach((overlay, shotIndex) => {
      const type = getShotTypeFromOverlay(overlay);
      const isAttack = isAttackOverlay(overlay);
      const isSoft = isSoftOverlay(overlay);

      if (!type && !isAttack) return;

      totalUsableShots++;

      if (isAttack) {
        attackShots++;
        rallyAttackShotIndices.push(shotIndex);
      }

      if (isSoft) {
        softShots++;
        rallySoftShotIndices.push(shotIndex);
      }
    });

    if (rallyAttackShotIndices.length >= 2) {
      attackRallyCount++;
      const winner = winningTeam(row);
      if (winner != null) attackWinsByTeam[winner]++;
      attackExamples.push(
        makeExample(
          rallyIndex,
          rallyAttackShotIndices.slice(0, 3),
          "Multiple attack-oriented shots",
          winner,
          shotPlayerId(row, rallyAttackShotIndices[0]),
        ),
      );
    }

    if (rallySoftShotIndices.length >= 2) {
      softRallyCount++;
      const winner = winningTeam(row);
      if (winner != null) softWinsByTeam[winner]++;
      softExamples.push(
        makeExample(
          rallyIndex,
          rallySoftShotIndices.slice(0, 4),
          "Multiple drops/dinks",
          winner,
          shotPlayerId(row, rallySoftShotIndices[0]),
        ),
      );
    }

    if (rallyAttackShotIndices.length >= 1 && rallySoftShotIndices.length >= 1) {
      mixedRallyCount++;
      const winner = winningTeam(row);
      if (winner != null) mixedWinsByTeam[winner]++;
      mixedExamples.push(
        makeExample(
          rallyIndex,
          [
            ...rallyAttackShotIndices.slice(0, 2),
            ...rallySoftShotIndices.slice(0, 2),
          ]
            .sort((a, b) => a - b),
          "Both attack and soft-exchange shots",
          winner,
          shotPlayerId(
            row,
            [
              ...rallyAttackShotIndices.slice(0, 1),
              ...rallySoftShotIndices.slice(0, 1),
            ].sort((a, b) => a - b)[0],
          ),
        ),
      );
    }
  });

  const attackPct =
    totalUsableShots === 0 ? 0 : attackShots / totalUsableShots;
  const softPct = totalUsableShots === 0 ? 0 : softShots / totalUsableShots;

  const isAttackHeavy = attackPct >= 0.35 && attackPct > softPct * 0.9;
  const isSoftHeavy = softPct >= 0.45 && softPct > attackPct * 1.2;
  const patternWinsByTeam: [number, number] = isAttackHeavy
    ? attackWinsByTeam
    : isSoftHeavy
      ? softWinsByTeam
      : mixedRallyCount > 0
        ? mixedWinsByTeam
        : [0, 0];
  const patternTotal = isAttackHeavy
    ? attackRallyCount
    : isSoftHeavy
      ? softRallyCount
      : mixedRallyCount;
  const edgeTeam = edgeTeamFromWins(patternWinsByTeam, patternTotal);
  const examples = (isAttackHeavy
    ? attackExamples
    : isSoftHeavy
      ? softExamples
      : mixedExamples.length
        ? mixedExamples
        : [...attackExamples, ...softExamples]
  )
    .sort(
      (a, b) =>
        exampleWinnerSort(rows, edgeTeam)(a, b) ||
        b.shotIndices.length - a.shotIndices.length ||
        rallyLength(rows[b.rallyIndex]) - rallyLength(rows[a.rallyIndex]),
    )
    .slice(0, 5);

  return {
    kind: "exchangeType",
    scope: "match",
    edgeTeam,
    label: labelWithEdge(
      isAttackHeavy
        ? "Drive-heavy exchanges"
        : isSoftHeavy
          ? "Soft-exchange game"
          : "Mixed exchanges",
      isAttackHeavy
        ? "Drive-heavy rallies favored"
        : isSoftHeavy
          ? "Soft exchanges favored"
          : "Mixed exchanges favored",
      patternWinsByTeam,
      patternTotal,
    ),
    quantity: quantityWithTeamSplit(
      `${Math.round(attackPct * 100)}% drives/smashes/speedups, ${Math.round(
        softPct * 100,
      )}% drops/dinks`,
      patternWinsByTeam,
      patternTotal,
    ),
    explanation: `${
      isAttackHeavy
        ? "Drives, speedups, and smashes were a major part of rally structure."
        : isSoftHeavy
          ? "Drops and dinks shaped much of the rally structure."
          : "No single exchange type clearly dominated."
    } ${teamEdgeText(patternWinsByTeam, patternTotal)}`,
    examples,
  };
}

function buildErrorEnvironmentRow(rows: KitchenArrivalRow[]): IdentityRow {
  const candidates: IdentityExample[] = [];
  let count = 0;
  const errorsByTeam: [number, number] = [0, 0];
  const knownTeamErrors: [number, number] = [0, 0];

  rows.forEach((row, i) => {
    const terminalError = terminalErrorInfo(row);
    if (!terminalError) return;

    count++;
    const team = terminalError.team ?? losingTeam(row);
    if (team != null) {
      errorsByTeam[team]++;
      if (terminalError.team != null) knownTeamErrors[team]++;
    }

    candidates.push(
      makeExample(
        i,
        [terminalError.shotIndex],
        `Point ended on ${terminalError.kind} fault`,
        team,
        terminalError.playerId,
      ),
    );
  });

  const pct = rows.length === 0 ? 0 : count / rows.length;
  const edgeTeam = edgeTeamFromWins(errorsByTeam, count);
  const examples = candidates
    .sort(
      (a, b) =>
        (edgeTeam != null
          ? (terminalErrorInfo(rows[a.rallyIndex])?.team ??
              losingTeam(rows[a.rallyIndex])) === edgeTeam
            ? -1
            : (terminalErrorInfo(rows[b.rallyIndex])?.team ??
                  losingTeam(rows[b.rallyIndex])) === edgeTeam
              ? 1
              : 0
          : 0) ||
        rallyLength(rows[a.rallyIndex]) - rallyLength(rows[b.rallyIndex]),
    )
    .slice(0, 5);

  return {
    kind: "errorEnvironment",
    scope: "match",
    edgeTeam,
    label: labelWithEdge(
      pct >= 0.45
        ? "Error-driven"
        : pct >= 0.3
          ? "Error-influenced"
          : "Cleaner execution",
      "More terminal errors from",
      errorsByTeam,
      count,
    ),
    quantity: quantityWithTeamSplit(
      `${pctText(count, rows.length)} ended on net/out faults`,
      errorsByTeam,
      count,
    ),
    explanation: `${
      pct >= 0.45
        ? "Many rallies ended on terminal execution errors."
        : pct >= 0.3
          ? "Errors shaped a meaningful share of points."
          : "Fewer rallies ended on obvious terminal errors."
    } ${teamEdgeText(errorsByTeam, count)}${
      knownTeamErrors[0] + knownTeamErrors[1] < count
        ? " Some terminal errors could only be assigned by losing team."
        : ""
    }`,
    examples,
  };
}

/*
========================================
Team rows
========================================
*/

function buildTeamShortRallyEdgeRow(
  rows: KitchenArrivalRow[],
  team: 0 | 1,
): IdentityRow | null {
  const SHORT_THRESHOLD = 4;

  let wins = 0;
  let total = 0;

  const candidates: IdentityExample[] = [];

  rows.forEach((row, i) => {
    if (rallyLength(row) > SHORT_THRESHOLD) return;

    const winner = winningTeam(row);
    if (winner == null) return;

    total++;

    if (winner === team) {
      wins++;

      candidates.push(
        makeExample(
          i,
          [],
          `Team ${team} won ${rallyLength(row)}-shot rally`,
          team,
          overlayPlayerId(lastOverlay(row)),
        ),
      );
    }
  });

  if (total < 4) return null;

  const pct = wins / total;
  if (pct < 0.6) return null;
  const examples = candidates
    .sort(
      (a, b) =>
        rallyLength(rows[a.rallyIndex]) - rallyLength(rows[b.rallyIndex]),
    )
    .slice(0, 4);

  return {
    kind: "teamShortRallyEdge",
    scope: "team",
    team,
    edgeTeam: team,
    label: `Team ${team} short-rally edge`,
    quantity: `${wins} of ${total} short rallies (${Math.round(pct * 100)}%)`,
    explanation: "This team won early-resolution exchanges more often.",
    examples,
  };
}

type TeamErrorLeakStats = {
  losses: number;
  errorLosses: number;
  pct: number;
};

function getTeamErrorLeakStats(
  rows: KitchenArrivalRow[],
  team: 0 | 1,
): TeamErrorLeakStats {
  let losses = 0;
  let errorLosses = 0;

  rows.forEach((row) => {
    const winner = winningTeam(row);
    if (winner == null) return;

    if (losingTeam(row) !== team) return;

    losses++;

    const terminalError = terminalErrorInfo(row);
    if (!terminalError) return;

    // Keep examples tied to this team's leak, not just any terminal error.
    if (terminalError.team != null && terminalError.team !== team) return;

    errorLosses++;
  });

  return {
    losses,
    errorLosses,
    pct: losses === 0 ? 0 : errorLosses / losses,
  };
}

function buildTeamErrorLeakRow(
  rows: KitchenArrivalRow[],
  team: 0 | 1,
): IdentityRow | null {
  const stats = getTeamErrorLeakStats(rows, team);
  const candidates: IdentityExample[] = [];

  rows.forEach((row, i) => {
    const winner = winningTeam(row);
    if (winner == null) return;
    if (losingTeam(row) !== team) return;

    const terminalError = terminalErrorInfo(row);
    if (!terminalError) return;
    if (terminalError.team != null && terminalError.team !== team) return;

    candidates.push(
      makeExample(
        i,
        [terminalError.shotIndex],
        `Team ${team} lost rally on ${terminalError.kind} fault`,
        team,
        terminalError.playerId,
      ),
    );
  });

  if (stats.losses < 4) return null;
  if (stats.pct < 0.45) return null;
  const examples = candidates
    .sort(
      (a, b) =>
        rallyLength(rows[a.rallyIndex]) - rallyLength(rows[b.rallyIndex]),
    )
    .slice(0, 5);

  return {
    kind: "teamErrorLeak",
    scope: "team",
    team,
    edgeTeam: team,
    label: `Team ${team} error leak`,
    quantity: `${stats.errorLosses} of ${stats.losses} losses ended on net/out faults (${Math.round(
      stats.pct * 100,
    )}%)`,
    explanation:
      "A large share of this team's lost rallies ended with terminal errors.",
    examples,
  };
}

/*
========================================
Sentence / builder
========================================
*/

function buildSentence(rows: IdentityRow[]) {
  const matchRows = rows.filter((r) => r.scope === "match");
  const teamRows = rows.filter((r) => r.scope === "team");
  const focusRows = rows.filter((r) => r.scope === "focus");

  if (focusRows.length > 0) {
    const focusSummary = focusRows
      .map((r) => r.label)
      .filter(Boolean)
      .join(". ");
    return focusSummary ? `${focusSummary}.` : "";
  }

  const rallyLengthRow = matchRows.find((r) => r.kind === "rallyLength");
  const kitchenRow = matchRows.find((r) => r.kind === "kitchenRole");
  const exchangeRow = matchRows.find((r) => r.kind === "exchangeType");
  const errorRow = matchRows.find((r) => r.kind === "errorEnvironment");
  const errorLeakRow = teamRows.find((r) => r.kind === "teamErrorLeak");
  const shortEdgeRow = teamRows.find((r) => r.kind === "teamShortRallyEdge");
  const clauses: string[] = [];

  if (kitchenRow?.edgeTeam != null && errorLeakRow?.team === kitchenRow.edgeTeam) {
    clauses.push(
      `Team ${kitchenRow.edgeTeam} won more kitchen-structured rallies, but also had the clearest terminal-error leak`,
    );
  } else if (kitchenRow?.edgeTeam != null && errorLeakRow?.team != null) {
    clauses.push(
      `Team ${kitchenRow.edgeTeam} did better once rallies reached the kitchen, while Team ${errorLeakRow.team} leaked more points on terminal errors`,
    );
  } else if (kitchenRow?.edgeTeam != null) {
    clauses.push(
      `Team ${kitchenRow.edgeTeam} had the clearest edge after rallies reached kitchen structure`,
    );
  } else if (errorLeakRow?.team != null) {
    clauses.push(
      `Team ${errorLeakRow.team} had the clearest terminal-error leak`,
    );
  } else if (errorRow?.edgeTeam != null) {
    clauses.push(`Team ${errorRow.edgeTeam} made more of the terminal errors`);
  }

  if (shortEdgeRow?.team != null) {
    clauses.push(`Team ${shortEdgeRow.team} was stronger in short rallies`);
  } else if (rallyLengthRow?.label === "Short-rally dominant") {
    clauses.push("many points were decided before rallies fully developed");
  } else if (rallyLengthRow?.label === "Short-rally leaning") {
    clauses.push("early exchanges mattered, but did not fully define the match");
  } else if (rallyLengthRow?.label === "Mixed rally length") {
    clauses.push("points were not mostly decided in the first four shots");
  } else if (rallyLengthRow?.label) {
    clauses.push(rallyLengthRow.label.toLowerCase());
  }

  if (
    exchangeRow?.edgeTeam != null &&
    exchangeRow.edgeTeam !== kitchenRow?.edgeTeam
  ) {
    clauses.push(`${exchangeRow.label.toLowerCase()}`);
  } else if (exchangeRow?.label) {
    clauses.push(`${exchangeRow.label.toLowerCase()} shaped the match`);
  }

  if (errorRow?.label && !clauses.some((clause) => clause.includes("error"))) {
    clauses.push(`${errorRow.label.toLowerCase()} points were a major theme`);
  }

  return clauses.length > 0
    ? `${clauses.map(sentenceCase).join(". ")}.`
    : matchRows
        .map((r) => r.label)
        .filter(Boolean)
        .join(", ");
}

function buildNextFocusRows(rows: IdentityRow[]): IdentityRow[] {
  const matchRows = rows.filter((r) => r.scope === "match");
  const teamRows = rows.filter((r) => r.scope === "team");
  const kitchenRow = matchRows.find((r) => r.kind === "kitchenRole");
  const exchangeRow = matchRows.find((r) => r.kind === "exchangeType");
  const errorRow = matchRows.find((r) => r.kind === "errorEnvironment");
  const focusRows: IdentityRow[] = [];

  ([0, 1] as const).forEach((team) => {
    const opponent = team === 0 ? 1 : 0;
    const ownErrorLeakRow = teamRows.find(
      (r) => r.kind === "teamErrorLeak" && r.team === team,
    );
    const ownShortEdgeRow = teamRows.find(
      (r) => r.kind === "teamShortRallyEdge" && r.team === team,
    );
    const opponentShortEdgeRow = teamRows.find(
      (r) => r.kind === "teamShortRallyEdge" && r.team === opponent,
    );
    const opponentErrorLeakRow = teamRows.find(
      (r) => r.kind === "teamErrorLeak" && r.team === opponent,
    );
    const kitchenExamplesForTeam =
      kitchenRow?.examples.filter((ex) => ex.team === team) ?? [];
    const kitchenExamplesForOpponent =
      kitchenRow?.examples.filter((ex) => ex.team === opponent) ?? [];

    if (ownErrorLeakRow) {
      const examples = ownErrorLeakRow.examples;
      const keptPositive =
        ownShortEdgeRow != null
          ? "Keep the short-rally edge, but stop giving points back with terminal misses."
          : kitchenRow?.edgeTeam === team
            ? "Keep building rallies to the kitchen, but clean up the rallies that end on free misses."
            : "Start by reducing the free endings before changing the overall style.";

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: team,
        label: `Team ${team}: review point-ending misses`,
        quantity: ownErrorLeakRow.quantity,
        explanation: `${keptPositive} In these clips, check whether the miss came from being rushed, off balance, choosing too much attack, or missing a routine ball.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
      return;
    }

    if (ownShortEdgeRow) {
      const examples = ownShortEdgeRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: team,
        label: `Team ${team}: repeat the early pressure`,
        quantity: ownShortEdgeRow.quantity,
        explanation: `Team ${team} was winning early-resolution rallies. Review these clips to identify the serve, return, or third-shot pattern that created quick pressure.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
      return;
    }

    if (opponentShortEdgeRow) {
      const examples = opponentShortEdgeRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: opponent,
        label: `Team ${team}: survive the first four shots`,
        quantity: opponentShortEdgeRow.quantity,
        explanation: `Team ${opponent} owned the short rallies. In these clips, find which early ball put Team ${team} under pressure and what safer reset, return, or third shot would have extended the point.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
      return;
    }

    if (kitchenRow?.edgeTeam === team) {
      const examples =
        kitchenExamplesForTeam.length > 0
          ? kitchenExamplesForTeam
          : kitchenRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: team,
        label: `Team ${team}: recreate kitchen control`,
        quantity: kitchenRow.quantity,
        explanation: `Team ${team} had the best edge once rallies reached kitchen structure. Review these clips to find the shot that got them settled there: drop, reset, dink location, or patience ball.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
      return;
    }

    if (kitchenRow?.edgeTeam === opponent) {
      const examples =
        kitchenExamplesForOpponent.length > 0
          ? kitchenExamplesForOpponent
          : kitchenRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: opponent,
        label: `Team ${team}: stop opponent kitchen control`,
        quantity: kitchenRow.quantity,
        explanation: `Team ${opponent} had the better results in kitchen-structured rallies. In these clips, find the ball that let them get comfortable at the kitchen, then look for a deeper push, lower dink, speedup choice, or reset denial.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
      return;
    }

    if (errorRow?.edgeTeam === team) {
      const examples = errorRow.examples.filter((ex) => ex.team === team);
      const rowExamples = examples.length > 0 ? examples : errorRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: team,
        label: `Team ${team}: review point-ending misses`,
        quantity: errorRow.quantity,
        explanation: `Team ${team} made more of the terminal errors. In these clips, check whether the miss came from pressure, balance, shot choice, or a routine execution miss before changing tactics.${playerFocusSuffix(
          rowExamples,
        )}`,
        examples: rowExamples,
      });
      return;
    }

    if (opponentErrorLeakRow) {
      const examples = opponentErrorLeakRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: opponent,
        label: `Team ${team}: keep forcing uncomfortable finishes`,
        quantity: opponentErrorLeakRow.quantity,
        explanation: `Team ${opponent} had the clearer terminal-error leak. Review these clips to find what made the final miss uncomfortable: depth, pace, location, body pressure, or patience.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
      return;
    }

    if (exchangeRow != null) {
      const examples = exchangeRow.examples;

      focusRows.push({
        kind: "nextFocus",
        scope: "focus",
        team,
        edgeTeam: exchangeRow.edgeTeam,
        label: `Team ${team}: identify the repeated rally pattern`,
        quantity: exchangeRow.quantity,
        explanation: `No stronger team-specific signal stood out for Team ${team}. Review these clips and name the repeated pattern: drive exchange, soft dink/drop exchange, speedup sequence, transition reset, or attack after a sitter.${playerFocusSuffix(
          examples,
        )}`,
        examples,
      });
    }
  });

  return focusRows;
}

function buildPlayerFocusCards(rows: IdentityRow[]): PlayerFocusCard[] {
  const focusRows = rows.filter((r) => r.scope === "focus");
  const nonFocusRows = rows.filter((r) => r.scope !== "focus");

  return [0, 1, 2, 3].map((playerId) => {
    const playerTeam = teamForPlayerId(playerId);
    let bestFocusLabel = "";
    let bestFocusExamples: IdentityExample[] = [];
    let bestFocusTeamExamples: IdentityExample[] = [];
    let bestSupportLabel = "";
    let bestSupportExamples: IdentityExample[] = [];
    let bestTeamFocusLabel = "";
    let bestTeamFocusExamples: IdentityExample[] = [];

    focusRows.forEach((row) => {
      if (
        row.team === playerTeam ||
        row.edgeTeam === playerTeam ||
        row.examples.some((ex) => ex.team === playerTeam)
      ) {
        const teamExamples = row.examples.filter((ex) => ex.team === playerTeam);
        const examples = teamExamples.length > 0 ? teamExamples : row.examples;
        if (examples.length > bestTeamFocusExamples.length) {
          bestTeamFocusLabel = playerFocusLabelFromRow(row).replace(
            "Primary focus:",
            "Shared team focus:",
          );
          bestTeamFocusExamples = examples;
        }
      }

      const examples = row.examples.filter((ex) => ex.playerId === playerId);
      if (examples.length <= bestFocusExamples.length) return;
      const teamExamples = row.examples.filter((ex) => ex.team === playerTeam);
      bestFocusLabel = playerFocusLabelFromRow(row);
      bestFocusExamples = examples;
      bestFocusTeamExamples =
        teamExamples.length > 0 ? teamExamples : row.examples;
    });

    if (bestFocusLabel && bestFocusExamples.length >= 2) {
      return {
        playerId,
        label: bestFocusLabel,
        detail: `Evidence: ${bestFocusExamples.length} focus ${
          bestFocusExamples.length === 1 ? "clip" : "clips"
        }`,
        examples: bestFocusExamples.slice(0, 8),
      };
    }

    nonFocusRows.forEach((row) => {
      const examples = row.examples.filter((ex) => ex.playerId === playerId);
      if (examples.length <= bestSupportExamples.length) return;
      bestSupportLabel = playerFocusLabelFromRow(row);
      bestSupportExamples = examples;
    });

    if (bestSupportLabel && bestSupportExamples.length >= 2) {
      return {
        playerId,
        label: bestSupportLabel,
        detail: `Evidence: ${bestSupportExamples.length} supporting ${
          bestSupportExamples.length === 1 ? "clip" : "clips"
        }`,
        examples: bestSupportExamples.slice(0, 8),
      };
    }

    if (bestFocusLabel && bestFocusTeamExamples.length > 1) {
      return {
        playerId,
        label: bestFocusLabel,
        detail: `Team evidence: ${bestFocusTeamExamples.length} clips; p${playerId} tagged in ${bestFocusExamples.length}`,
        examples: bestFocusTeamExamples.slice(0, 8),
      };
    }

    if (bestFocusLabel && bestFocusExamples.length === 1) {
      return {
        playerId,
        label: "Limited individual evidence",
        detail: `Only 1 tagged clip for p${playerId}.`,
        examples: bestFocusExamples,
      };
    }

    if (bestTeamFocusLabel && bestTeamFocusExamples.length > 0) {
      return {
        playerId,
        label: bestTeamFocusLabel,
        detail: `Team-level cue: ${bestTeamFocusExamples.length} evidence ${
          bestTeamFocusExamples.length === 1 ? "clip" : "clips"
        }`,
        examples: bestTeamFocusExamples.slice(0, 8),
      };
    }

    return {
      playerId,
      label: "No clear individual cue",
      detail: "Evidence did not isolate this player.",
      examples: [],
    };
  });
}

function computeIdentity(rows: KitchenArrivalRow[]): IdentityResult {
  const identityRows: IdentityRow[] = [];
  const rallyLengthRow = buildShortRallyRow(rows);

  if (
    rallyLengthRow.label !== "Mixed rally length" ||
    rallyLengthRow.edgeTeam != null
  ) {
    identityRows.push(rallyLengthRow);
  }

  identityRows.push(buildKitchenRoleRow(rows));
  identityRows.push(buildExchangeTypeRow(rows));
  identityRows.push(buildErrorEnvironmentRow(rows));

  const shortRallyRows = [
    buildTeamShortRallyEdgeRow(rows, 0),
    buildTeamShortRallyEdgeRow(rows, 1),
  ].filter((x): x is IdentityRow => Boolean(x));
  const errorLeakRows = [
    buildTeamErrorLeakRow(rows, 0),
    buildTeamErrorLeakRow(rows, 1),
  ].filter((x): x is IdentityRow => Boolean(x));
  const errorLeakStats = [
    getTeamErrorLeakStats(rows, 0),
    getTeamErrorLeakStats(rows, 1),
  ] as const;
  const filteredErrorLeakRows =
    errorLeakRows.length === 2
      ? Math.abs(errorLeakStats[0].pct - errorLeakStats[1].pct) >= 0.1
        ? [
            errorLeakStats[0].pct > errorLeakStats[1].pct
              ? errorLeakRows.find((r) => r.team === 0)
              : errorLeakRows.find((r) => r.team === 1),
          ].filter((x): x is IdentityRow => Boolean(x))
        : []
      : errorLeakRows;

  identityRows.push(...shortRallyRows, ...filteredErrorLeakRows);
  identityRows.unshift(...buildNextFocusRows(identityRows));

  return {
    oneSentence: buildSentence(identityRows),
    rows: identityRows,
  };
}

/*
========================================
Popup Component
========================================
*/

export function MatchIdentityPopup(props: {
  open: boolean;
  onClose: () => void;

  packageId: string;
  rows: KitchenArrivalRow[];

  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity: RenderPlayerIdentity;

  firefightsClips: any[];
  firefightRallySet: Set<number>;
}) {
  const { open, onClose, rows, packageId } = props;

  const identity = React.useMemo(() => computeIdentity(rows), [rows]);
  const playerFocusCards = React.useMemo(
    () => buildPlayerFocusCards(identity.rows),
    [identity],
  );

  const [selectedExampleByPlayer, setSelectedExampleByPlayer] = React.useState<
    Record<number, IdentityExample | null>
  >({});
  const [focusedPlayerId, setFocusedPlayerId] = React.useState<number | null>(
    null,
  );
  const [helpPlayerId, setHelpPlayerId] = React.useState<number | null>(null);
  const [popupPos, setPopupPos] = React.useState({ left: 80, top: 80 });
  const dragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startLeft: number;
    startTop: number;
  } | null>(null);

  React.useEffect(() => {
    if (!open) return;

    setSelectedExampleByPlayer({});
    setFocusedPlayerId(null);
    setHelpPlayerId(null);
  }, [open, identity]);

  const handleHeaderPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest("button")) return;

      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startLeft: popupPos.left,
        startTop: popupPos.top,
      };
    },
    [popupPos],
  );

  const handleHeaderPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      setPopupPos({
        left: Math.max(0, drag.startLeft + event.clientX - drag.startX),
        top: Math.max(0, drag.startTop + event.clientY - drag.startY),
      });
    },
    [],
  );

  const handleHeaderPointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;

      dragRef.current = null;
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    [],
  );

  const getCardExample = React.useCallback(
    (card: PlayerFocusCard) => {
      const selectedExample = selectedExampleByPlayer[card.playerId];
      return selectedExample &&
        card.examples.some((ex) => ex.key === selectedExample.key)
        ? selectedExample
        : card.examples[0] ?? null;
    },
    [selectedExampleByPlayer],
  );

  const renderPlayerVideoPanel = React.useCallback(
    (card: PlayerFocusCard, compact: boolean) => {
      const example = getCardExample(card);
      const row = example == null ? null : rows[example.rallyIndex];
      const showHelp = helpPlayerId === card.playerId;
      const focusHelp = focusHelpForLabel(card.label);

      return (
        <Box
          key={card.playerId}
          sx={{
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            border: "1px solid rgba(255,255,255,0.22)",
            bgcolor: "black",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: compact ? 0.75 : 1,
              py: compact ? 0.5 : 0.75,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              color: "white",
              borderBottom: "1px solid rgba(255,255,255,0.18)",
            }}
          >
            {props.renderPlayerIdentity({
              playerId: card.playerId,
              size: compact ? 46 : 64,
              borderRadius: 7,
              fontSize: compact ? 12 : 16,
              fontColor: "white",
            })}

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: compact ? 10 : 13,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {card.label}
              </Typography>

              <Typography
                sx={{
                  fontSize: compact ? 9 : 11,
                  lineHeight: 1.1,
                  color: "rgba(255,255,255,0.72)",
                }}
              >
                {card.detail}
              </Typography>
            </Box>

            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setFocusedPlayerId(compact ? card.playerId : null)
              }
              sx={{
                minWidth: 0,
                px: compact ? 0.5 : 0.75,
                py: 0,
                fontSize: compact ? 9 : 10,
                color: "white",
                borderColor: "rgba(255,255,255,0.45)",
              }}
            >
              {compact ? "Focus" : "All"}
            </Button>
          </Box>

          <Box sx={{ px: 0.5, py: 0.35 }}>
            <Button
              size="small"
              variant={showHelp ? "contained" : "outlined"}
              onClick={() =>
                setHelpPlayerId(showHelp ? null : card.playerId)
              }
              sx={{
                minWidth: 0,
                px: 0.5,
                py: 0,
                fontSize: compact ? 9 : 10,
                lineHeight: 1.1,
                color: showHelp ? "black" : "white",
                borderColor: "rgba(255,255,255,0.45)",
                bgcolor: showHelp ? "rgba(255,255,255,0.9)" : "transparent",
              }}
            >
              What do you mean?
            </Button>
          </Box>

          {showHelp ? (
            <Box
              sx={{
                mx: 0.5,
                mb: 0.35,
                p: compact ? 0.5 : 0.75,
                color: "white",
                bgcolor: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.16)",
              }}
            >
              <Typography sx={{ fontSize: compact ? 9 : 11, lineHeight: 1.2 }}>
                <b>Means:</b> {focusHelp.means}
              </Typography>
              <Typography sx={{ fontSize: compact ? 9 : 11, lineHeight: 1.2, mt: 0.35 }}>
                <b>Try:</b> {focusHelp.drills}
              </Typography>
            </Box>
          ) : null}

          <Box sx={{ px: 0.5, py: 0.35, display: "flex", gap: 0.35, flexWrap: "wrap" }}>
            {card.examples.slice(0, compact ? 3 : 6).map((ex) => {
              const selected = example?.key === ex.key;

              return (
                <Button
                  key={ex.key}
                  size="small"
                  variant={selected ? "contained" : "text"}
                  onClick={() =>
                    setSelectedExampleByPlayer((prev) => ({
                      ...prev,
                      [card.playerId]: ex,
                    }))
                  }
                  sx={{
                    textTransform: "uppercase",
                    fontWeight: 800,
                    minWidth: 0,
                    px: 0.35,
                    py: 0,
                    fontSize: compact ? 9 : 10,
                    lineHeight: 1,
                  }}
                >
                  R{ex.rallyNumber}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            {row ? (
              <ActiveRallyVideo
                packageId={packageId}
                row={row}
                rows={rows}
                insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
                renderPlayerIdentity={props.renderPlayerIdentity}
                aiContextLabel={`Match Identity p${card.playerId}`}
                focusedFilter={null}
                activeShotNumber={null}
                showCondensedTimeline={false}
                fontColor="white"
                controlsPlacement="left"
                firefightsClips={props.firefightsClips}
                firefightRallySet={props.firefightRallySet}
                showRateControls={!compact}
                showTitle={true}
                showScoreEtc={true}
                showAi={!compact}
                aiDrawerMode="split"
                showLoopScrubber={!compact}
                defaultLoopUiOpen={false}
                showPopoutButton={true}
                isInFullPopup={false}
              />
            ) : (
              <Box sx={{ p: 2, color: "white", fontSize: 12 }}>
                No player-specific evidence clips.
              </Box>
            )}
          </Box>
        </Box>
      );
    },
    [getCardExample, helpPlayerId, packageId, props, rows],
  );

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: popupPos.left,
        top: popupPos.top,
        width: 1220,
        maxWidth: "calc(100vw - 36px)",
        height: "88vh",
        maxHeight: "calc(100vh - 80px)",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        overflow: "hidden",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        onPointerMove={handleHeaderPointerMove}
        onPointerUp={handleHeaderPointerUp}
        onPointerCancel={handleHeaderPointerUp}
        sx={{
          px: 1.5,
          py: 0.5,
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "move",
          userSelect: "none",
        }}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 15 }}>
          Player Focus Review
        </Typography>

        <Button size="small" onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </Box>

      <Box sx={{ px: 1.25, py: 0.5 }}>
        <Box
          sx={{
            mb: 0.65,
            p: 0.75,
            border: "1px solid rgba(0,0,0,0.10)",
            bgcolor: "rgba(0,0,0,0.025)",
            borderRadius: 1,
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: 12, mb: 0.25 }}>
            Experiment note
          </Typography>
          <Typography sx={{ fontSize: 11.5, lineHeight: 1.25, color: "rgba(0,0,0,0.68)" }}>
            The purpose of this experiment was to see whether a match could get a fast deterministic identity: what kind of game it was, which team had the clearest edge or leak, and what a player might review first. It became more interesting when it moved toward player-specific focus clips, but the language still needs to become more actionable and less like generic analytics.
          </Typography>
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: 13, lineHeight: 1.15 }}>
          {identity.oneSentence}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            lineHeight: 1.15,
            color: "rgba(0,0,0,0.65)",
            mt: 0.25,
          }}
        >
          Each panel shows one player's primary review cue with a few evidence clips. Use Focus to expand a player.
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: "black", p: 0.75 }}>
          {focusedPlayerId == null ? (
            <Box
              sx={{
                height: "100%",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gridTemplateRows: "1fr 1fr",
                gap: 0.75,
                minHeight: 0,
              }}
            >
              {playerFocusCards.map((card) =>
                renderPlayerVideoPanel(card, true),
              )}
            </Box>
          ) : (
            (() => {
              const focusedCard =
                playerFocusCards.find(
                  (card) => card.playerId === focusedPlayerId,
                ) ?? playerFocusCards[0];

              return focusedCard
                ? renderPlayerVideoPanel(focusedCard, false)
                : null;
            })()
          )}
        </Box>
      </Box>
    </Paper>
  );
}
